<template>
  <div class="chat-message-item" :class="active ? 'active' : ''">
    <div class="message-tip" v-if="msgInfo.type == $enums.MESSAGE_TYPE.TIP_TEXT">
      {{ msgInfo.content }}
    </div>
    <div class="message-tip" v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.TIP_TIME">
      {{ $date.toTimeText(msgInfo.sendTime) }}
    </div>
    <div class="message-normal " v-else-if="isNormal" :class="{ 'message-mine': mine }">
      <div class="avatar">
        <head-image :name="showName" :size="38" :url="headImage" :id="msgInfo.sendId"></head-image>
      </div>
      <div class="content">
        <div v-if="msgInfo.groupId && !msgInfo.selfSend" class="top">
          <div class="show-name">{{ showName }}</div>
          <el-tag v-if="isGroupOwner(msgInfo.sendId)" size="mini" type="danger">群主</el-tag>
          <el-tag v-if="isGroupManager(msgInfo.sendId)" size="mini" type="primary">管理员</el-tag>
        </div>
        <div class="bottom" :class="{ fullscreen: configStore.fullScreen }"
             @contextmenu.prevent="showMessageMenu($event)">
          <div ref="chatMsgBox" class="message-content-wrapper">
            <div class="message-text" v-if="isTextMessage" v-html="htmlText">
            </div>
            <div class="message-image" v-else-if="msgInfo.type === $enums.MESSAGE_TYPE.IMAGE"
                 @click="showFullImageBox()">
              <div class="image-container" :style="imageStyle">
                <img class="send-image" :src="contentData.thumbUrl" loading="lazy" />
                <!-- 上传进度条 -->
                <div v-if="showUploadProgress" class="upload-progress-overlay">
                  <el-progress
                      type="circle"
                      :percentage="uploadProgress"
                      :width="40"
                      :stroke-width="4"
                      :show-text="false">
                  </el-progress>
                </div>
                <div class="image-overlay" v-if="!showUploadProgress">
                  <i class="el-icon-zoom-in"></i>
                </div>
              </div>
            </div>
            <div class="message-video" v-else-if="msgInfo.type === $enums.MESSAGE_TYPE.VIDEO">
              <!-- 视频上传进度 -->
              <div class="video-upload-wrapper">
                <!-- fix #190: 监听播放结束，若 HLS 实际 duration 明显短于原视频 duration，提示用户视频可能被截断 -->
                <video class="send-video" ref="videoPlayer" :style="imageStyle" :controls="videoPlaying" preload="none"
                       @ended="onVideoEnded"
                       :poster="contentData.localCover||contentData.coverUrl" :src="contentData.localFile||contentData.videoUrl" />
                <!-- 未播放时的覆盖层：就绪显示播放按钮 / 未就绪显示 loading -->
                <div
                    v-if="!videoPlaying && !showUploadProgress"
                    class="video-play-overlay"
                    @click="onPlayVideo"
                >
                  <div v-if="videoReady" class="play-button-container">
                    <i class="el-icon-video-play play-icon"></i>
                  </div>
                  <div v-else class="hls-loading">
                    <div class="hls-spinner"></div>
                  </div>
                </div>
                <div v-if="showUploadProgress" class="video-upload-progress">
                  <el-progress
                      type="circle"
                      :percentage="uploadProgress"
                      :width="40"
                      :stroke-width="4"
                      :show-text="false">
                  </el-progress>
                </div>
              </div>
            </div>
            <div class="message-file" v-else-if="msgInfo.type === $enums.MESSAGE_TYPE.FILE">
              <div class="file-box" :v-loading="sending && !showUploadProgress">
                <div class="file-info">
                  <el-link class="file-name" :underline="true" target="_blank" type="primary"
                           :href="contentData.url" :download="contentData.name">{{ contentData.name
                    }}</el-link>
                  <div class="file-size">{{ fileSize }}</div>
                  <!-- 文件上传进度 -->
                  <div v-if="showUploadProgress" class="file-upload-progress">
                    <el-progress
                        :percentage="uploadProgress"
                        :stroke-width="4"
                        :show-text="false">
                    </el-progress>
                  </div>
                </div>
                <div class="file-icon">
                  <span type="primary" class="el-icon-document"></span>
                </div>
              </div>
            </div>
            <div class="message-voice" v-else-if="msgInfo.type === $enums.MESSAGE_TYPE.AUDIO"
                 @click="onPlayVoice()">
              <audio controls :src="contentData.url"></audio>
            </div>
            <!-- 原有的发送状态指示器 -->
            <div title="发送中" v-if="sending && msgInfo.type === $enums.MESSAGE_TYPE.TEXT" class="sending" v-loading="sending"></div>
            <div title="发送失败" v-else-if="sendFail" @click="onSendFail" class="send-fail el-icon-warning">
            </div>
          </div>
          <chat-user-card v-if="msgInfo.type === $enums.MESSAGE_TYPE.USER_CARD"
                          :cardInfo="contentData"></chat-user-card>
          <chat-group-card v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.GROUP_CARD" :cardInfo="contentData"
                           :sendTime="msgInfo.sendTime"></chat-group-card>
          <div class="chat-action message-text" v-else-if="isAction">
						<span v-if="msgInfo.type == $enums.MESSAGE_TYPE.ACT_RT_VOICE" title="重新呼叫"
                  @click="$emit('call')" class="iconfont icon-chat-voice"></span>
            <span v-if="msgInfo.type == $enums.MESSAGE_TYPE.ACT_RT_VIDEO" title="重新呼叫"
                  @click="$emit('call')" class="iconfont icon-chat-video"></span>
            <span>{{ msgInfo.content }}</span>
          </div>
          <div class="quote-message" v-if="msgInfo.quoteMessage"
               @contextmenu.prevent.stop="showQuoteMenu($event)">
            <chat-quote-message :msgInfo="msgInfo.quoteMessage"
                                @click.native.stop="$emit('locateQuote', msgInfo)"
                                :showName="quoteShowName"></chat-quote-message>
          </div>
          <div class="message-status" v-if="!isAction && msgInfo.selfSend && !isGroupMessage">
            <span class="chat-readed" v-if="msgInfo.status == $enums.MESSAGE_STATUS.READED">已读</span>
            <span class="chat-unread" v-else>未读</span>
          </div>
          <div class="chat-receipt" v-if="msgInfo.receipt" @click="onShowReadedBox">
            <span v-if="msgInfo.receiptOk" class="icon iconfont icon-ok" title="全体已读"></span>
            <span v-else>{{ msgInfo.readedCount }}人已读</span>
          </div>
        </div>
      </div>
    </div>
    <right-menu ref="rightMenu" @select="onSelectMenu"></right-menu>
    <chat-group-readed ref="chatGroupReadedBox" :msgInfo="msgInfo" :group="group"
                       :groupMembers="groupMembers"></chat-group-readed>
  </div>
</template>

<script>
import HeadImage from "../common/HeadImage.vue";
import RightMenu from '../common/RightMenu.vue';
import ChatGroupReaded from './ChatGroupReaded.vue';
import ChatQuoteMessage from "./ChatQuoteMessage.vue";
import ChatUserCard from "./ChatUserCard.vue";
import ChatGroupCard from "./ChatGroupCard.vue";
import downloader from "@/utils/downloader";
import Hls from "hls.js";
import Vue from "vue";
import {fileFromDataUri} from "@/utils/imageUtil";
export default {
  name: "messageItem",
  components: {
    HeadImage,
    RightMenu,
    ChatGroupReaded,
    ChatQuoteMessage,
    ChatUserCard,
    ChatGroupCard
  },
  props: {
    mode: {
      type: Number,
      default: 1
    },
    active: {
      type: Boolean,
      default: false
    },
    mine: {
      type: Boolean,
      required: true
    },
    headImage: {
      type: String,
      default: ''
    },
    showName: {
      type: String,
      required: true
    },
    quoteShowName: {
      type: String,
      default: ''
    },
    msgInfo: {
      type: Object,
      required: true
    },
    group: {
      type: Object,
    },
    groupMembers: {
      type: Array
    }
  },
  data() {
    return {
      audio: null,
      audioPlaying: false,
      videoPlaying: false,
      hlsInstance: null
    }
  },
  methods: {
    onSendFail() {
      this.$emit("resend", this.msgInfo);
    },
    showFullImageBox() {
      if (this.showUploadProgress) {
        return;
      }
      const { originUrl, thumbUrl } = this.contentData;
      if (originUrl) {
        this.$eventBus.$emit("openFullImage", {
          msgId: this.msgInfo.id,
          originUrl,
          thumbUrl
        });
      }
    },
    onPlayVoice() {
      if (this.audioPlaying) {
        this.audio.pause();
        this.audioPlaying = false;
        return;
      }

      if (!this.audio) {
        this.audio = new Audio();
        this.audio.onended = () => {
          this.audioPlaying = false;
        };
        this.audio.onpause = () => {
          this.audioPlaying = false;
        };
        this.audio.onplay = () => {
          this.audioPlaying = true;
        };
      }
      this.audio.src = this.contentData.url;
      this.audio.play();
    },
    async onPlayVideo() {
      const video = this.$refs.videoPlayer
      console.log('[VIDEO_HLS] ▶ onPlayVideo messageId:', this.msgInfo.id, 'content:', this.contentData)

      // 1) 自己发的消息本机就有 localFile
      if (this.contentData.localFile) {
        const ok = await this.verifyLocalFile(this.contentData.localFile)
        if (ok) {
          console.log('[VIDEO_HLS] ✓ 命中 content.localFile 直接播放')
          this.videoPlaying = true
          video.play()
          return
        }
        this.clearInvalidLocalFile()
      }

      // 2) prewarm 就绪的 m3u8
      const e = this.prewarmEntry
      if (e && e.status === 'ready' && this.contentData.m3u8Url) {
        console.log('[VIDEO_HLS] ✓ prewarm 就绪, 直接 playHls')
        this.playHls(this.contentData.m3u8Url)
        return
      }

      // 3) prewarm 已下载完成的本地文件兜底
      if (e && e.localFile) {
        const ok = await this.verifyLocalFile(e.localFile)
        if (ok) {
          console.log('[VIDEO_HLS] ✓ prewarm localFile 兜底播放')
          this.videoPlaying = true
          video.src = e.localFile
          video.play()
          return
        }
      }

      // 4) 旧消息无 m3u8Url：直接走网络 videoUrl
      if (!this.contentData.m3u8Url && this.contentData.videoUrl) {
        console.log('[VIDEO_HLS] ⚙ 旧消息无 m3u8Url, 网络播放 videoUrl')
        this.videoPlaying = true
        video.src = this.contentData.videoUrl
        video.play()
        return
      }

      // 5) m3u8 未就绪：触发一次兜底预热 + 展示 loading（保持 videoPlaying=false, overlay 上的 spinner 会继续显示）
      console.log('[VIDEO_HLS] ⚙ m3u8 未就绪, 等待预热完成自动播放')
      this._waitingForReady = true
      try {
        if (this.videoPrewarmStore && !this.prewarmEntry) {
          this.videoPrewarmStore.prewarm(this.msgInfo)
        }
      } catch (err) {
        /* ignore */
      }
    },
    async verifyLocalFile(fileUrl) {
      if (!window.electronAPI || !fileUrl) return false
      try {
        return await window.electronAPI.invoke('file-exists', fileUrl)
      } catch (e) {
        return false
      }
    },
    clearInvalidLocalFile() {
      const data = JSON.parse(this.msgInfo.content)
      if (data.localFile) delete data.localFile
      if (data.localCover) delete data.localCover
      Vue.set(this.msgInfo, 'content', JSON.stringify(data))
      this.persistMessage()
    },
    persistMessage() {
      const chat = this.chatStore && this.chatStore.activeChat
      if (chat) {
        console.log('[VIDEO_HLS] → 派发 chatStore.updateMessage 持久化 messageId:', this.msgInfo.id)
        this.chatStore.updateMessage(this.msgInfo, chat)
        console.log('[VIDEO_HLS] ✓ 持久化完成')
      } else {
        console.warn('[VIDEO_HLS] ✗ persistMessage 未找到 activeChat, 跳过持久化')
      }
    },
    tryPlayM3u8(m3u8Url) {
      console.log('[VIDEO_HLS] ▶ tryPlayM3u8 开始轮询 HEAD (5s 间隔, 非 404 视为就绪):', m3u8Url)
      Vue.set(this.msgInfo, 'progress', 0)
      Vue.set(this.msgInfo, 'status', this.$enums.MESSAGE_STATUS.DOWNLOADING)
      this._m3u8Cancelled = false
      const poll = () => {
        if (this._m3u8Cancelled) return
        fetch(m3u8Url, { method: 'HEAD' })
          .then(res => {
            if (this._m3u8Cancelled) return
            console.log('[VIDEO_HLS] ← m3u8 HEAD 响应', res.status)
            // 非 404 即视为已切片就绪
            if (res.status !== 404 && res.status < 500) {
              Vue.set(this.msgInfo, 'status', this.$enums.MESSAGE_STATUS.DELIVERED)
              console.log('[VIDEO_HLS] ✓ m3u8 就绪 (status=' + res.status + '), 进入 playHls')
              this.playHls(m3u8Url)
            } else {
              Vue.set(this.msgInfo, 'progress', Math.min((this.msgInfo.progress || 0) + 3, 90))
              this._m3u8PollTimer = setTimeout(poll, 5000)
            }
          })
          .catch((e) => {
            if (this._m3u8Cancelled) return
            console.warn('[VIDEO_HLS] ⚙ m3u8 HEAD 失败 5s 后重试:', e && e.message)
            Vue.set(this.msgInfo, 'progress', Math.min((this.msgInfo.progress || 0) + 3, 90))
            this._m3u8PollTimer = setTimeout(poll, 5000)
          })
      }
      poll()
      this._m3u8Timeout = setTimeout(() => {
        if (!this._m3u8Cancelled) {
          console.warn('[VIDEO_HLS] ✗ m3u8 轮询 180s 超时, 降级完整下载')
          this.cancelM3u8Poll()
          this.startDownloadFile('video')
        }
      }, 180000)
    },
    cancelM3u8Poll() {
      this._m3u8Cancelled = true
      if (this._m3u8PollTimer) clearTimeout(this._m3u8PollTimer)
      if (this._m3u8Timeout) clearTimeout(this._m3u8Timeout)
    },
    // fix #190: 视频播放结束时校验实际 duration vs 原视频 duration，若差异 > 10% 提示视频可能不完整
    onVideoEnded() {
      try {
        const video = this.$refs.videoPlayer
        const actual = video && video.duration
        const expected = this.contentData && this.contentData.duration
        if (actual && expected && expected > 0 && actual < expected * 0.9) {
          console.warn('[VIDEO_HLS] ⚠ duration mismatch - actual:', actual, 'expected:', expected, 'gap:', (expected - actual).toFixed(2), 's')
          this.$message && this.$message({
            message: `视频可能不完整（实际 ${actual.toFixed(0)}s / 原片 ${expected.toFixed(0)}s），可下载原视频查看`,
            type: 'warning',
            duration: 4000
          })
        }
      } catch (e) { /* ignore */ }
    },
    playHls(hlsUrl) {
      console.log('[VIDEO_HLS] ▶ playHls 加载 m3u8:', hlsUrl, 'Hls.isSupported=', Hls.isSupported())
      const video = this.$refs.videoPlayer
      if (Hls.isSupported()) {
        if (this.hlsInstance) {
          this.hlsInstance.destroy()
        }
        const hls = new Hls()
        this.hlsInstance = hls
        hls.loadSource(hlsUrl)
        hls.attachMedia(video)
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          console.log('[VIDEO_HLS] ✓ hls manifest parsed, 开始播放')
          this.videoPlaying = true
          video.play()
        })
        hls.on(Hls.Events.FRAG_LOADED, (event, data) => {
          console.log('[VIDEO_HLS] ← 分段加载完成 seq:', data && data.frag && data.frag.sn, 'url:', data && data.frag && data.frag.url)
        })
        hls.on(Hls.Events.ERROR, (event, data) => {
          console.error('[VIDEO_HLS] ✗ hls error fatal=', data.fatal, 'type=', data.type, 'details=', data.details)
          if (data.fatal) {
            hls.destroy()
            this.hlsInstance = null
            console.warn('[VIDEO_HLS] ✗ hls fatal 降级完整下载')
            this.startDownloadFile('video')
          }
        })
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        console.log('[VIDEO_HLS] ⚙ 走原生 HLS (canPlayType application/vnd.apple.mpegurl)')
        video.src = hlsUrl
        this.videoPlaying = true
        video.play()
      } else {
        console.warn('[VIDEO_HLS] ✗ 不支持 HLS, 降级完整下载')
        this.startDownloadFile('video')
      }
    },
    showMessageMenu(e) {
      if (this.showUploadProgress) {
        return;
      }
      let menuItems = [];
      if (this.isTextMessage) {
        menuItems.push({
          key: 'COPY',
          name: '复制'
        });
      }
      menuItems.push({
        key: 'DELETE',
        name: '删除'
      });
      if (this.msgInfo.id) {
        const canRecallByRole = this.isOwner || this.isManager || this.msgInfo.selfSend;
        const withinRecallWindow = this.msgInfo.sendTime
          && (Date.now() - this.msgInfo.sendTime <= 2 * 60 * 1000);
        if (canRecallByRole && withinRecallWindow) {
          menuItems.push({
            key: 'RECALL',
            name: '撤回'
          });
        }
        if (this.$msgType.isNormal(this.msgInfo.type)) {
          menuItems.push({
            key: 'QUOTE',
            name: '引用'
          });
          menuItems.push({
            key: 'FORWARD',
            name: '转发'
          });
        }
        if ((this.isOwner || this.isManager) && this.$msgType.isNormal(this.msgInfo.type)) {
          menuItems.push({
            key: 'TOP',
            name: '置顶'
          });
        }
      }
      this.$refs.rightMenu.open(e, menuItems);
    },
    showQuoteMenu(e) {
      let menuItems = [];
      if (this.msgInfo.quoteMessage &&
          this.msgInfo.quoteMessage.status != this.$enums.MESSAGE_STATUS.RECALL) {
        menuItems.push({
          key: 'LOCATE_QUOTE',
          name: '定位到原消息'
        });
      }
      this.$refs.rightMenu.open(e, menuItems);
    },
    onSelectMenu(item) {
      let eventKey = item.key.toLowerCase().replace(/_([a-z])/g, (g) => g[1].toUpperCase());
      this.$emit(eventKey, this.msgInfo);
    },
    onShowReadedBox() {
      let rect = this.$refs.chatMsgBox.getBoundingClientRect();
      this.$refs.chatGroupReadedBox.open(rect);
    },
    isGroupOwner(userId) {
      return this.group.ownerId == userId;
    },
    isGroupManager(userId) {
      let m = this.groupMembers.find(m => m.userId == userId);
      return m && m.isManager
    },
    startDownloadFile(fileType){
      console.log('[VIDEO_HLS] ▶ startDownloadFile 完整下载 fileType:', fileType, 'url:', this.contentData.videoUrl)
      Vue.set(this.msgInfo, 'progress', 0)
      Vue.set(this.msgInfo, 'status', this.$enums.MESSAGE_STATUS.DOWNLOADING)
      const downloadId = downloader.download({
        url: this.contentData.videoUrl,
        fileName: this.contentData.fileName,
        fileType:fileType,
        onStart: (info) => {
          console.log('[VIDEO_HLS] → 下载开始:', info)
        },
        onProgress: (progress) => {
          // this.msgInfo.progress=progress.progress
          Vue.set(this.msgInfo, 'progress', progress.progress)
        },
        onComplete: async (result) => {
          if(fileType!=='file'){
            this.downloadCover(this.contentData.coverUrl,fileType,result.filePath)
          }else {
            Vue.set(this.msgInfo, 'status', this.$enums.MESSAGE_STATUS.DELIVERED)
            console.log('[VIDEO_HLS] ✓ 完整下载完成 filePath:', result.filePath)
            let originContent = JSON.parse(this.msgInfo.content)
            let data = Object.assign({}, originContent, {
              localFile: `file:///${result.filePath.replace(/\\/g, '/')}`,
            })
            Vue.set(this.msgInfo, 'content', JSON.stringify(data))
            console.log('[VIDEO_HLS] ⚙ 注入 localFile 到 content:', data.localFile)
            this.persistMessage()
          }

        },
        onError: (error) => {
          this.message.error("文件下载失败："+error);
        }
      })
    },
    downloadCover(path,fileType,loacalFilePath){
      downloader.download({
        url: path,
        fileName: this.contentData.fileName,
        fileType:fileType,
        onStart: (info) => {
          console.log('开始下载：',info)
        },
        onProgress: (progress) => {
          // this.msgInfo.progress=progress.progress
          // Vue.set(this.msgInfo, 'progress', progress.progress)
        },
        onComplete: async (result) => {
          Vue.set(this.msgInfo, 'status', this.$enums.MESSAGE_STATUS.DELIVERED)
          console.log('[VIDEO_HLS] ✓ 封面下载完成 filePath:', result.filePath)
          let originContent = JSON.parse(this.msgInfo.content)
          let data = Object.assign({}, originContent, {
            localFile: `file:///${loacalFilePath.replace(/\\/g, '/')}`,
            localCover: `file:///${result.filePath.replace(/\\/g, '/')}`,
          })
          Vue.set(this.msgInfo, 'content', JSON.stringify(data))
          console.log('[VIDEO_HLS] ⚙ 注入 localFile + localCover, 持久化')
          this.persistMessage()
        },
        onError: (error) => {
          this.message.error("文件下载失败："+error);
        }
      })
    }
  },
  computed: {
    sending() {
      return this.msgInfo.status === this.$enums.MESSAGE_STATUS.SENDING;
    },
    downloading(){
      return this.msgInfo.status === this.$enums.MESSAGE_STATUS.DOWNLOADING;
    },
    sendFail() {
      return this.msgInfo.status === this.$enums.MESSAGE_STATUS.FAILED;
    },
    contentData() {

      return JSON.parse(this.msgInfo.content)
    },
    prewarmEntry() {
      const id = this.msgInfo && this.msgInfo.id
      if (!id || !this.videoPrewarmStore) return null
      return this.videoPrewarmStore.getEntry(id)
    },
    videoReady() {
      // 自己发的视频消息本机就有 localFile（发送时写入），不需要预热
      if (this.contentData && this.contentData.localFile) return true
      // 无 m3u8Url 的旧消息按旧行为放行
      if (this.contentData && !this.contentData.m3u8Url) return true
      const e = this.prewarmEntry
      return !!(e && (e.status === 'ready' || e.localFile))
    },
    uploadProgress() {
      return Number(this.msgInfo.progress) || 0;
    },
    showUploadProgress() {
      return ((this.sending && this.uploadProgress < 100)||this.downloading && this.uploadProgress < 100 )&&
          (this.msgInfo.type === this.$enums.MESSAGE_TYPE.IMAGE ||
              this.msgInfo.type === this.$enums.MESSAGE_TYPE.VIDEO ||
              this.msgInfo.type === this.$enums.MESSAGE_TYPE.FILE);
    },
    fileSize() {
      let size = this.contentData.size;
      if (size > 1024 * 1024) {
        return Math.round(size / 1024 / 1024) + "M";
      }
      if (size > 1024) {
        return Math.round(size / 1024) + "KB";
      }
      return size + "B";
    },
    isTextMessage() {
      return this.msgInfo.type == this.$enums.MESSAGE_TYPE.TEXT
    },
    isAction() {
      return this.$msgType.isAction(this.msgInfo.type);
    },
    isNormal() {
      const type = this.msgInfo.type;
      return this.$msgType.isNormal(type) || this.$msgType.isAction(type)
    },
    isOwner() {
      let userId = this.userStore.userInfo.id;
      return this.group && userId == this.group.ownerId
    },
    isManager() {
      let userId = this.userStore.userInfo.id;
      let m = this.groupMembers.find((m) => m.userId == userId);
      return m && m.isManager;
    },
    htmlText() {
      let color = this.msgInfo.selfSend ? 'white' : '';
      let text = this.$str.html2Escape(this.msgInfo.content)
      text = this.$url.replaceURLWithHTMLLinks(text, color)
      return this.$emo.transform(text, 'emoji-normal')
    },
    isGroupMessage() {
      return !!this.msgInfo.groupId;
    },
    imageStyle() {
      let maxSize = this.configStore.fullScreen ? 360 : 240;
      let minSize = 60;
      let width = this.contentData.width;
      let height = this.contentData.height;
      if (width && height) {
        let ratio = Math.min(width, height) / Math.max(width, height);
        let w = Math.max(Math.min(width > height ? maxSize : ratio * maxSize, width), minSize);
        let h = Math.max(Math.min(width > height ? ratio * maxSize : maxSize, height), minSize);
        return `width: ${w}px;height:${h}px;object-fit: cover;`
      } else {
        return `max-width: ${maxSize}px;min-width:60px;max-height: ${maxSize}px;min-height:60px;`
      }
    }
  },
  watch: {
    'prewarmEntry.status'(val) {
      if (this._waitingForReady && val === 'ready' && this.contentData.m3u8Url) {
        this._waitingForReady = false
        console.log('[VIDEO_HLS] ✓ prewarm 等待就绪, 自动 playHls')
        this.playHls(this.contentData.m3u8Url)
      }
    },
    'prewarmEntry.localFile'(val) {
      if (this._waitingForReady && val) {
        this._waitingForReady = false
        const video = this.$refs.videoPlayer
        if (video) {
          this.videoPlaying = true
          video.src = val
          video.play()
        }
      }
    }
  },
  beforeDestroy() {
    if (this.hlsInstance) {
      this.hlsInstance.destroy()
      this.hlsInstance = null
    }
    this.cancelM3u8Poll()
  }
}
</script>

<style lang="scss" scoped>
.chat-message-item {
  padding: 3px 10px;
  border-radius: 10px;

  &.active {
    background: var(--im-background-active-dark);
  }

  .message-tip {
    line-height: 50px;
    font-size: var(--im-font-size-small);
    color: var(--im-text-color-light);
    word-break: break-word;
  }

  .message-normal {
    position: relative;
    font-size: 0;
    padding-left: 53px;
    min-height: 50px;
    margin: 5px 0;

    .avatar {
      position: absolute;
      width: 40px;
      height: 40px;
      top: 0;
      left: 0;
    }

    .content {
      text-align: left;

      .top {
        display: flex;
        flex-wrap: nowrap;
        align-items: center;

        .show-name {
          white-space: nowrap;
          max-width: 400px;
          overflow: hidden;
          line-height: 18px;
          font-size: var(--im-font-size-small);
          color: #888;
        }
      }

      .bottom {
        display: inline-block;
        padding-right: 30px;
        margin-top: 2px;

        &.fullscreen {
          padding-right: 240px;
        }

        .message-content-wrapper {
          position: relative;
          display: inline-flex;
          align-items: flex-end;

          .sending {
            width: 25px;
            height: 25px;
            position: absolute;
            right: -30px;
            bottom: 0;

            ::v-deep .circular {
              width: 25px;
              height: 25px;
            }
          }

          .send-fail {
            color: #e45050;
            font-size: 25px;
            cursor: pointer;
            margin: 0 5px;
            position: absolute;
            right: -30px;
            bottom: 0;
          }
        }

        .message-text {
          flex: 1;
          display: inline-block;
          position: relative;
          line-height: 26px;
          padding: 6px 10px;
          background: var(--im-background);
          border-radius: 10px;
          font-size: var(--im-font-size);
          text-align: left;
          white-space: pre-wrap;
          word-break: break-word;
        }

        .message-image {
          border-radius: 12px;
          overflow: hidden;
          cursor: pointer;
          background: var(--im-background);
          box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
          transition: all 0.3s ease;
          position: relative;

          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
          }

          .image-container {
            position: relative;
            width: 100%;
            height: 100%;
            overflow: hidden;
            border-radius: 12px;

            .send-image {
              width: 100%;
              height: 100%;
              object-fit: cover;
              transition: transform 0.3s ease;
            }

            // 图片上传进度
            .upload-progress-overlay {
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: rgba(0, 0, 0, 0.5);
              display: flex;
              align-items: center;
              justify-content: center;
              border-radius: 12px;

              .el-progress {
                .el-progress-circle__track {
                  stroke: rgba(255, 255, 255, 0.3);
                }
                .el-progress-circle__path {
                  stroke: #ffffff;
                }
                .el-progress__text {
                  color: white;
                  font-size: 10px;
                }
              }
            }

            .image-overlay {
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: rgba(0, 0, 0, 0.3);
              display: flex;
              align-items: center;
              justify-content: center;
              opacity: 0;
              transition: opacity 0.3s ease;

              i {
                color: white;
                font-size: 24px;
              }
            }

            &:hover {
              .send-image {
                transform: scale(1.05);
              }

              .image-overlay {
                opacity: 1;
              }
            }
          }
        }

        .message-video {
          border-radius: 12px;
          overflow: hidden;
          background: var(--im-background);
          box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
          transition: all 0.3s ease;

          // 视频上传包装器
          .video-upload-wrapper {
            position: relative;
            width: 100%;
            height: 100%;
          }

          // 视频上传进度
          .video-upload-progress {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 12px;

            .el-progress {
              .el-progress-circle__track {
                stroke: rgba(255, 255, 255, 0.3);
              }
              .el-progress-circle__path {
                stroke: #ffffff;
              }
              .el-progress__text {
                color: white;
                font-size: 10px;
              }
            }
          }

          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
          }

          .send-video {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.3s ease;
          }

          &:hover .send-video {
            transform: scale(1.02);
          }

          .video-play-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.3); /* 半透明黑色背景 */
            display: flex;
            justify-content: center;
            align-items: center;
            cursor: pointer;
            z-index: 10;
            transition: background-color 0.3s ease;
            border-radius: 4px; /* 如果需要圆角 */
          }

          .video-play-overlay:hover {
            background-color: rgba(0, 0, 0, 0.4); /* 悬停时稍微加深 */
          }

          .play-button-container {
            background-color: rgba(255, 255, 255, 0.9); /* 按钮背景 */
            border-radius: 50%;
            display: flex;
            justify-content: center;
            align-items: center;
            transition: all 0.3s ease;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }

          .play-button-container:hover {
            transform: scale(1.1);
            background-color: rgba(255, 255, 255, 1);
            box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
          }

          .play-icon {
            font-size: 28px;
            color: #333;
            margin-left: 4px; /* 让图标稍微向右偏移，看起来更居中 */
          }

          .hls-loading {
            display: flex;
            justify-content: center;
            align-items: center;

            .hls-spinner {
              width: 36px;
              height: 36px;
              border: 3px solid rgba(255, 255, 255, 0.35);
              border-top-color: #fff;
              border-radius: 50%;
              animation: hls-spin 0.9s linear infinite;
            }
          }

          @keyframes hls-spin {
            to { transform: rotate(360deg); }
          }
        }

        .message-file {
          display: flex;
          flex-wrap: nowrap;
          flex-direction: row;
          align-items: center;
          cursor: pointer;
          margin-bottom: 2px;
          background: var(--im-background);

          .file-box {
            display: flex;
            flex-wrap: nowrap;
            align-items: center;
            min-height: 60px;
            box-shadow: var(--im-box-shadow-light);
            border-radius: 8px;
            padding: 10px 15px;
            border: 2px solid #eee;
            transition: all 0.3s ease;
            background: white;

            &:hover {
              transform: translateY(-2px);
              box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
            }

            .file-info {
              flex: 1;
              height: 100%;
              text-align: left;
              font-size: 14px;
              margin-right: 10px;

              .file-name {
                display: inline-block;
                min-width: 160px;
                max-width: 200px;
                font-size: 14px;
                margin-bottom: 4px;
                white-space: pre-wrap;
                word-break: break-all;
              }

              .file-size {
                font-size: var(--im-font-size-smaller);
                color: var(--im-text-color-light);
              }

              // 文件上传进度
              .file-upload-progress {
                margin-top: 8px;
                width: 100%;

                .el-progress {
                  .el-progress-bar {
                    .el-progress-bar__outer {
                      background-color: #f0f0f0;
                      border-radius: 3px;
                    }
                    .el-progress-bar__inner {
                      background-color: #409EFF;
                      border-radius: 3px;
                    }
                  }
                }

                .progress-text {
                  font-size: 12px;
                  color: #409EFF;
                  text-align: center;
                  margin-top: 4px;
                }
              }
            }

            .file-icon {
              font-size: 44px;
              color: #d42e07;
              transition: transform 0.3s ease;
            }

            &:hover .file-icon {
              transform: scale(1.1);
            }
          }
        }

        .message-voice {
          audio {
            height: 46px;
            cursor: pointer;
            border: 1px solid rgba(0, 0, 0, 0.08);
            border-radius: 23px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
            transition: all 0.3s ease;

            &:hover {
              transform: translateY(-1px);
              box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
            }
          }
        }

        .chat-action {
          display: flex;
          align-items: center;

          .iconfont {
            cursor: pointer;
            font-size: 22px;
            padding-right: 8px;
          }
        }

        .quote-message {
          display: block;
          margin-top: 3px;
          cursor: pointer;
        }

        .message-status {
          margin-top: 3px;
          display: block;
          font-size: 11px;

          .chat-readed {
            color: var(--im-text-color-light);
          }

          .chat-unread {
            color: var(--im-color-danger);
          }
        }

        .chat-receipt {
          font-size: var(--im-font-size-smaller);
          cursor: pointer;
          color: var(--im-text-color-light);

          .icon-ok {
            font-size: 20px;
            color: var(--im-color-success);
          }
        }

        .chat-at-user {
          padding: 2px 5px;
          border-radius: 3px;
          cursor: pointer;
        }
      }
    }

    &.message-mine {
      text-align: right;
      padding-left: 0;
      padding-right: 53px;

      .avatar {
        left: auto;
        right: 0;
      }

      .content {
        text-align: right;

        .top {
          flex-direction: row-reverse;
        }

        .bottom {
          padding-left: 30px;
          padding-right: 0;

          &.fullscreen {
            padding-left: 240px;
          }

          .message-content-wrapper {
            flex-direction: row-reverse;

            .sending {
              right: auto;
              left: -30px;
            }

            .send-fail {
              right: auto;
              left: -30px;
            }
          }

          .message-text {
            background-color: var(--im-color-primary-light-2);
            color: #fff;
          }

          .chat-action {
            flex-direction: row-reverse;

            .iconfont {
              transform: rotateY(180deg);
            }
          }
        }
      }
    }
  }
}
</style>